<script setup>
import { computed } from 'vue';
import BaseAttachmentBubble from './BaseAttachment.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';

const { attachments, contentAttributes } = useMessageContext();
const { t } = useI18n();

const attachment = computed(() => {
  return attachments.value[0];
});

const lat = computed(() => {
  return attachment.value.coordinatesLat ?? attachment.value.coordinates_lat;
});
const long = computed(() => {
  return attachment.value.coordinatesLong ?? attachment.value.coordinates_long;
});

const title = computed(() => {
  return attachment.value.fallbackTitle ?? attachment.value.fallback_title;
});

const address = computed(() => {
  const location =
    contentAttributes.value?.whatsappLocation ||
    contentAttributes.value?.whatsapp_location ||
    {};
  return location.address || '';
});

const hasCoordinates = computed(
  () => Number(lat.value) !== 0 || Number(long.value) !== 0
);

const mapUrl = computed(
  () => `https://maps.google.com/?q=${lat.value},${long.value}`
);

// Keyless Google embed: renders the WhatsApp-style map preview card.
const mapEmbedUrl = computed(
  () =>
    `https://maps.google.com/maps?q=${lat.value},${long.value}&z=15&output=embed`
);
</script>

<template>
  <div
    v-if="hasCoordinates"
    class="w-72 max-w-full overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
  >
    <a
      :href="mapUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="relative block h-36"
    >
      <iframe
        :src="mapEmbedUrl"
        class="h-full w-full border-0 pointer-events-none"
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
        :title="title || t('COMPONENTS.LOCATION_BUBBLE.SEE_ON_MAP')"
      />
    </a>
    <div class="flex flex-col gap-0.5 px-3 py-2">
      <span v-if="title" class="text-sm font-medium text-n-slate-12 truncate">
        {{ title }}
      </span>
      <a
        :href="mapUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="text-xs text-n-slate-11 hover:underline truncate"
      >
        {{ address || t('COMPONENTS.LOCATION_BUBBLE.SEE_ON_MAP') }}
      </a>
    </div>
  </div>
  <BaseAttachmentBubble
    v-else
    icon="i-ph-navigation-arrow-fill"
    icon-bg-color="bg-[#0D9B8A]"
    sender-translation-key="CONVERSATION.SHARED_ATTACHMENT.LOCATION"
    :content="title"
    :action="{
      label: t('COMPONENTS.LOCATION_BUBBLE.SEE_ON_MAP'),
      href: mapUrl,
    }"
  />
</template>
